import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { forgotPassword } from "@/services/authService";

// Form schema
const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Initialize form
  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  // Handle form submission
  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setIsSubmitting(true);
    try {
      // Call the forgotPassword service
      const response = await forgotPassword(data.email);
      
      if (response.success) {
        setEmailSent(true);
        toast({
          title: "Email Sent",
          description: response.message || "If an account exists with this email, you will receive password reset instructions.",
          variant: "default",
        });
      } else {
        // Handle unsuccessful response but don't reveal if email exists
        setEmailSent(true);
        toast({
          title: "Email Sent",
          description: "If an account exists with this email, you will receive password reset instructions.",
          variant: "default",
        });
      }
    } catch (error) {
      console.error("Password reset request failed:", error);
      // We don't want to reveal if an email exists in our system for security reasons
      // So we still show a success message even if the email doesn't exist
      setEmailSent(true);
      toast({
        title: "Email Sent",
        description: "If an account exists with this email, you will receive password reset instructions.",
        variant: "default",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => navigate("/signin")}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to login</span>
            </Button>
            <div className="flex-1 text-center">
              <CardTitle className="text-2xl font-bold">Forgot Password</CardTitle>
            </div>
            <div className="w-8"></div> {/* Spacer for alignment */}
          </div>
          <CardDescription>
            Enter your email address and we'll send you instructions to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailSent ? (
            <div className="text-center space-y-4">
              <div className="mx-auto bg-green-100 rounded-full p-3 w-12 h-12 flex items-center justify-center">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-medium">Check your email</h3>
              <p className="text-sm text-muted-foreground">
                We've sent password reset instructions to your email address. Please check your inbox.
              </p>
              <Button
                variant="outline"
                className="mt-4 w-full"
                onClick={() => navigate("/signin")}
              >
                Return to Sign In
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="your.email@example.com" 
                          type="email" 
                          {...field} 
                          autoComplete="email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : "Send Reset Instructions"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <div className="text-center text-sm">
            <span className="text-muted-foreground">Remember your password? </span>
            <Link 
              to="/signin" 
              className="text-primary hover:underline"
            >
              Sign in
            </Link>
            <span className="text-muted-foreground"> or </span>
            <Link 
              to="/signup" 
              className="text-primary hover:underline"
            >
              Create an account
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ForgotPassword;